import { Ability } from '@casl/ability';
import { useGetResourcesPaginated } from '@dispatch/shared/hooks';
import {
  Actions,
  ITaskMessage,
  MultiSelectOption,
  Subjects,
} from '@dispatch/shared/types';
import { AsyncSelect, Form, RenderFuncProps } from '@dispatch/web/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';

export const ForceReassignTasksDialog = ({
  showModal,
  permissions,
  handleClose,
  onForceReassign,
  selectedTasks,
}: {
  showModal: boolean;
  permissions: Ability<[Actions, Subjects]> | undefined;
  handleClose: () => void;
  onForceReassign?: (
    value: { resource: MultiSelectOption },
    selectedTasks?: ITaskMessage[]
  ) => void;
  selectedTasks?: ITaskMessage[];
}) => {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<{
    resource: MultiSelectOption;
  }>();

  const [resourceInput, setResourceInput] = useState<string>('');
  const [resourceDebouncedValue, setResourceDebouncedValue] =
    useState<string>('');

  useDebounce(
    () => {
      setResourceDebouncedValue(resourceInput);
    },
    1000,
    [resourceInput]
  );

  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: resourceDebouncedValue,
      isActive: true,
      pageSize: 25, //search only first 25
    },
    enabled: !!resourceDebouncedValue,
  });

  const mappedFilteredResources = useMemo((): MultiSelectOption[] | void => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data?.map((resource): MultiSelectOption => {
        return {
          title: resource.name,
          value: resource.integrationGUID,
        };
      });
    }
  }, [filteredResources.data, filteredResources.status]);

  const handleForceReassign = () => {
    onForceReassign &&
      handleSubmit((data) => {
        onForceReassign(data, selectedTasks);
      })().then(() => {
        handleClose();
        reset();
      });
  };

  return (
    <Dialog open={showModal} onClose={handleClose} maxWidth={'sm'} fullWidth>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={'baseline'}
        paddingRight={'2%'}
      >
        <DialogTitle>Re-assign All Tasks</DialogTitle>
        <HighlightOffIcon
          sx={{
            '&:hover': { color: 'red' },
          }}
          onClick={() => handleClose()}
        />
      </Stack>
      <DialogContent sx={{ minHeight: '30vh' }}>
        <Form<{ resource: MultiSelectOption }>
          formId={'resourceTasksForm'}
          errors={errors}
          control={control}
          handleSubmit={handleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <div style={{ width: '100%' }}>
                <AsyncSelect
                  id={'resource'}
                  name={'resource'}
                  label={'Assign Tasks to:'}
                  control={control}
                  readOnly={!hasUpsertPermissions}
                  errors={errors}
                  options={mappedFilteredResources || []}
                  setInput={setResourceInput}
                />
              </div>
            );
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="submit" onClick={handleForceReassign} disabled={!isDirty}>
          Re-Assign All
        </Button>
      </DialogActions>
    </Dialog>
  );
};
